import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { filter, Subject, takeUntil } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { Navigation, SwiperOptions } from 'swiper';
import { IToolbarData } from 'src/app/shared/models/components-data.models';
import { ItemStatus } from 'src/app/shared/models/item.models';
import { ServicesStore } from '../services.store';
import { successServicesModalData } from '../../jobs/jobs';
import { environment } from 'src/environments/environment';
import { IUser } from 'src/app/auth/auth.models';
import { UserService } from 'src/app/auth/user.service';
import { PopoverController } from '@ionic/angular';
import { ShareOptionsComponent } from '../../../../activity-feed/post-details/share-options/share-options.component';

const TOOLBAR_DATA: IToolbarData = {
  arrowRouter: '/bazaar/job&services/services',
  btnSlot: 'end',
  title: 'Service Seekers',
};

const TOOLBAR_SWIPER: IToolbarData = {
  arrow: true,
  btnSlot: 'end',
  title: 'Service Seekers',
};

@Component({
  selector: 'app-services-details',
  templateUrl: './services-details.component.html',
  styleUrls: ['./services-details.component.scss'],
})
export class ServicesDetailsComponent implements OnInit, OnDestroy {
  @ViewChild('col') col: ElementRef;

  TOOLBAR_DATA = TOOLBAR_DATA;
  TOOLBAR_SWIPER = TOOLBAR_SWIPER;
  config: SwiperOptions;

  serviceItem;
  currentUserID;
  creator;
  currentUser: IUser;
  isAdmin: boolean;

  status: ItemStatus;
  duration;
  currentTime: Date = new Date();
  itemImage: string;

  flagData;
  isContentFlaggedModal;
  isOpenFlagModal;

  isOpen = false;
  showSuccessModal = false;
  successModalData = successServicesModalData;
  isOpenFormModal = false;
  infoSnapshot;
  userInfo;
  showStatusModal: boolean = false;
  itemStatus;
  galleryUrls: string;
  isOpenGallery = false;

  discordServerInviteLink = environment.discordServerInviteLink;
  itemId: string;
  private destroy$ = new Subject<void>();

  constructor(
    private servicesStore: ServicesStore,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private userService: UserService,
    private popoverController: PopoverController
  ) {}

  ngOnInit() {
    this.itemId = this.activatedRoute.snapshot.params.id;
    this.getUser();
    const app: HTMLElement = document.querySelector('ion-router-outlet');
    app.style.marginBottom = '0px';
    this.servicesStore.getItemByByValue({
      documentId: this.itemId,
    });
    this.listenJobsDetails();

    this.servicesStore.selectShowFormModal().subscribe(isOpen => {
      this.isOpenFormModal = isOpen;
    });

    this.servicesStore.selectShowPendingModal().subscribe(isOpen => {
      this.showSuccessModal = isOpen;
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.infoSnapshot) {
      this.infoSnapshot.unsubscribe();
    }
  }

  handleOk(e: boolean) {
    e ? this.servicesStore.updateShowPendingModal(false) : null;
  }

  handleCancel(e: boolean) {
    e ? this.servicesStore.updateShowPendingModal(false) : null;
  }

  openModal() {
    this.servicesStore.updateShowFormModal(true);
  }

  closeModal(e: boolean) {
    e ? this.servicesStore.updateShowFormModal(false) : null;
  }

  openStatusModal() {
    this.showStatusModal = !this.showStatusModal;
  }

  async flagContent(slide) {
    slide.type = 'services';

    this.flagData = slide;

    const functions = getFunctions();
    const isContentFlagged = httpsCallable(functions, 'isContentFlagged');
    const res = await isContentFlagged({
      itemId: slide.id,
      url: slide.images[0],
      type: this.flagData.type,
    });
    if (res.data === true) {
      this.isContentFlaggedModal = true;
    } else {
      this.isOpenFlagModal = true;
    }
  }

  private listenJobsDetails() {
    this.servicesStore
      .selectItem()
      .pipe(
        takeUntil(this.destroy$),
        filter(item => !!item),
        switchMap(item => {
          this.serviceItem = item;

          this.itemImage = this.serviceItem.images[0];

          return this.userService.getUserByUid(item.creator.id);
        }),
        filter(user => !!user)
      )
      .subscribe(user => {
        this.creator = user;
        this.galleryUrls = this.serviceItem.images.map(
          pic => `url("${pic}") no-repeat center center / cover`
        );
      });
  }

  toggleGallery(i?) {
    this.isOpenGallery = !this.isOpenGallery;
    this.config = {
      modules: [Navigation],
      loop: true,
      navigation: true,
      initialSlide: i,
    };
  }

  private getUser() {
    this.userService.currentUser$
      .pipe(
        takeUntil(this.destroy$),
        filter(user => !!user.uid)
      )
      .subscribe(user => {
        this.currentUser = user;
        this.currentUserID = user.uid;
        this.isAdmin = this.userService.isAdmin;
        this.userInfo = this.userService.balanceInfo;
      });
  }

  openChat() {
    if (this.creator?.id) {
      this.router.navigate(['/chat/detail', this.creator.id]);
    }
  }

  async shareItem(event: Event) {
    event.stopPropagation();
    const postUrl = window.location.href;
    const postTitle = this.serviceItem?.title || 'MerMapp Service';
    const postText =
      this.serviceItem?.description || 'Check out this service on MerMapp!';

    const popover = await this.popoverController.create({
      component: ShareOptionsComponent,
      componentProps: {
        postUrl: postUrl,
        postTitle: postTitle,
        postText: postText,
        onOptionSelected: (option: string) => {
          this.handleShareOption(option, postUrl, postTitle, postText);
          popover.dismiss();
        },
      },
      event: event,
      translucent: true,
      backdropDismiss: true,
      alignment: 'center',
      side: 'top',
      arrow: true,
    });

    await popover.present();
  }

  private async handleShareOption(
    option: string,
    postUrl: string,
    postTitle: string,
    postText: string
  ) {
    switch (option) {
      case 'copy':
        await navigator.clipboard.writeText(postUrl);
        break;
      case 'facebook':
        window.open(
          `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
            postUrl
          )}`,
          '_blank'
        );
        break;
      case 'twitter':
        window.open(
          `https://twitter.com/intent/tweet?url=${encodeURIComponent(
            postUrl
          )}&text=${encodeURIComponent(postText)}`,
          '_blank'
        );
        break;
      case 'whatsapp':
        window.open(
          `https://api.whatsapp.com/send?text=${encodeURIComponent(
            `${postText} ${postUrl}`
          )}`,
          '_blank'
        );
        break;
      case 'email':
        window.open(
          `mailto:?subject=${encodeURIComponent(
            postTitle
          )}&body=${encodeURIComponent(`${postText} ${postUrl}`)}`,
          '_blank'
        );
        break;
      case 'native':
        if (navigator.share) {
          try {
            await navigator.share({
              title: postTitle,
              text: postText,
              url: postUrl,
            });
          } catch (err) {
            console.error('Error sharing:', err);
          }
        }
        break;
    }
  }
}

